import React, { useState, useMemo } from 'react';
import { SWORN_DATA } from '../../gameData';
import { KnightKey, AnvilUpgrade, UITheme } from '../../types';
import { getIconPath } from '../../utils/iconPath';
import { MorganaToggle, MorganaToggleOption } from '../ui/MorganaToggle';
import { SelectableItem } from '../ui/SelectableItem';
import { SelectionPanel } from '../ui/SelectionPanel';

interface AnvilPanelProps {
  mode: 'single' | 'double';
  setMode: (mode: 'single' | 'double') => void;
  selectedUpgrades: string[];
  toggleUpgrade: (upgradeName: string) => void;
  selectedKnight: KnightKey | null;
  selectedWeapon: string | null;
  selectedSpell: string | null;
}

const AnvilPanel: React.FC<AnvilPanelProps> = ({
  mode,
  setMode,
  selectedUpgrades,
  toggleUpgrade,
  selectedKnight,
  selectedWeapon,
  selectedSpell
}) => {
  const theme = (SWORN_DATA.ui_theme?.colors || { morgana_gold: '#e6a84f', text_black: '#000000' }) as UITheme['colors'];
  const [viewMode, setViewMode] = useState<'weapon' | 'spell'>('weapon');
  
  // Safely access the upgrades for the current weapon or spell
  const upgrades = useMemo((): AnvilUpgrade[] => {
    if (!selectedKnight) return [];
    
    if (viewMode === 'weapon' && selectedWeapon) {
      return SWORN_DATA.classes[selectedKnight]?.weapons[selectedWeapon]?.anvil_upgrades || [];
    } else if (viewMode === 'spell' && selectedSpell) {
      return SWORN_DATA.classes[selectedKnight]?.spells[selectedSpell]?.anvil_upgrades || [];
    }
    return [];
  }, [selectedKnight, selectedWeapon, selectedSpell, viewMode]);

  return (
    <SelectionPanel
        title="Anvil Upgrades"
        titleColor={theme.morgana_gold}
        toggleOptions={[{ label: 'Single Mod', value: 'single' }, { label: 'Double Mod', value: 'double' }] as [MorganaToggleOption<'single' | 'double'>, MorganaToggleOption<'single' | 'double'>]}
        toggleValue={mode}
        onToggleChange={setMode}
        toggleColors={{ primary: theme.morgana_gold, textOnPrimary: theme.text_black }}
        containerId="anvil-panel"
    >
      {/* View Mode Toggle (Weapon vs Spell) */}
      <MorganaToggle
        options={[{ label: 'Weapon Mods', value: 'weapon' }, { label: 'Spell Mods', value: 'spell' }]}
        value={viewMode}
        onChange={setViewMode}
        colors={{ primary: theme.morgana_gold, textOnPrimary: theme.text_black }}
      />

      <div className="mt-4 flex flex-col gap-2">
        {upgrades.map((upgrade: AnvilUpgrade) => {
          const selectionCount = selectedUpgrades.filter(m => m === upgrade.name).length;
          const isSelected = selectionCount > 0;
          
          // Determine icon path options based on view mode
          let iconPath = '';
          if (selectedKnight) {
            if (viewMode === 'weapon' && selectedWeapon) {
              iconPath = getIconPath('anvil_weapon', upgrade.name, { knight: selectedKnight, weapon: selectedWeapon });
            } else if (viewMode === 'spell' && selectedSpell) {
              iconPath = getIconPath('anvil_spell', upgrade.name, { knight: selectedKnight, spell: selectedSpell });
            }
          }

          return (
            <SelectableItem
                key={upgrade.name}
                id={upgrade.name}
                title={upgrade.name}
                subtitle={upgrade.effect}
                iconPath={iconPath}
                isSelected={isSelected}
                badgeText={selectionCount > 1 ? 'x2' : 'Active'}
                themeColor={theme.morgana_gold}
                onClick={toggleUpgrade}
            />
          );
        })}
        {upgrades.length === 0 && (
            <div style={{ color: '#ffffff', textAlign: 'center', padding: '20px' }}>
                {viewMode === 'weapon' && !selectedWeapon && "Please select a weapon."}
                {viewMode === 'spell' && !selectedSpell && "Please select a spell."}
                {((viewMode === 'weapon' && selectedWeapon) || (viewMode === 'spell' && selectedSpell)) && "No upgrades found."}
            </div>
        )}
      </div>
    </SelectionPanel>
    );
};

export default AnvilPanel;